import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeEvent } from '../../contexts/RealtimeContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Search, Wrench, CheckCircle, AlertTriangle,
  Calendar, Settings, FileText,
  Bell, Download, ChevronLeft, Camera,
  PieChart, Users, CalendarDays, FileSpreadsheet, Shield
} from 'lucide-react';

import { MONTHS_RO, MONTHS_SHORT } from './preventive/constants';
import { MonthlySheet } from './preventive/MonthlySheet';
import { OverdueDashboardView } from './preventive/OverdueDashboardView';
import { BulkValidationView } from './preventive/BulkValidationView';
import { KpiDashboardView } from './preventive/KpiDashboardView';
import { TechnicianHistoryView } from './preventive/TechnicianHistoryView';
import { CalendarView } from './preventive/CalendarView';
import { GenerateDialog } from './preventive/GenerateDialog';
import { QrScannerDialog } from './preventive/QrScannerDialog';
import { SettingsDialog } from './preventive/SettingsDialog';

const PreventiveEntriesPage = () => {
  const { user, hasRole } = useAuth();
  const [equipment, setEquipment] = useState([]);
  const [maintenanceTypes, setMaintenanceTypes] = useState([]);
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedEquipmentId, setSelectedEquipmentId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genMonth, setGenMonth] = useState(String(new Date().getMonth() + 1));
  const [generating, setGenerating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState('types');
  const [showTypeForm, setShowTypeForm] = useState(false);
  const [editType, setEditType] = useState(null);
  const [typeForm, setTypeForm] = useState({ name: '', description: '', operatiuni: '' });
  const [alertConfig, setAlertConfig] = useState({
    enabled: true, days_before_month_end: 5, days_after_month_end: 3,
    notify_on_validation: true, recipients: [], rules: [],
    schedule_hour: 8, schedule_days: ['luni', 'miercuri', 'vineri']
  });
  const [newRecipient, setNewRecipient] = useState({ name: '', email: '' });
  const [alertDashboard, setAlertDashboard] = useState(null);
  const [alertHistory, setAlertHistory] = useState([]);
  const [sendingAlerts, setSendingAlerts] = useState(false);
  const [mobileView, setMobileView] = useState('list');
  const [showScanner, setShowScanner] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const qrScannerRef = useRef(null);
  const [rightView, setRightView] = useState('equipment');
  const [kpiData, setKpiData] = useState(null);
  const [techData, setTechData] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [showHeaderConfig, setShowHeaderConfig] = useState(false);
  const [headerConfig, setHeaderConfig] = useState({ cod_document: '', anexa: '', override_date: '' });
  const [overdueData, setOverdueData] = useState(null);
  const [pendingEntries, setPendingEntries] = useState([]);
  const [bulkMonth, setBulkMonth] = useState('');
  const [bulkTechId, setBulkTechId] = useState('');
  const [bulkTechName, setBulkTechName] = useState('');
  const [bulkDateMode, setBulkDateMode] = useState('random');
  const [bulkCustomDate, setBulkCustomDate] = useState('');
  const [bulkSelected, setBulkSelected] = useState([]);
  const [bulkValidating, setBulkValidating] = useState(false);
  const [bulkAction, setBulkAction] = useState('validate');
  const [bulkStatusFilter, setBulkStatusFilter] = useState('pending');
  const [bulkOperatorName, setBulkOperatorName] = useState('');
  const [bulkNewStatus, setBulkNewStatus] = useState('pending');
  const [bulkDateField, setBulkDateField] = useState('both');

  const fetchBase = useCallback(async () => {
    try {
      const [typesRes, eqRes, usersRes] = await Promise.all([
        api.get('/preventive/maintenance-types'),
        api.get('/equipment'),
        api.get('/users'),
      ]);
      setMaintenanceTypes(typesRes.data);
      setEquipment(eqRes.data.filter(e => e.is_active !== false));
      setUsers(usersRes.data.filter(u => u.is_active !== false));
    } catch (e) { console.error(e); }
  }, []);

  const fetchEntries = useCallback(async () => {
    if (!selectedEquipmentId) { setEntries([]); setLoading(false); return; }
    try {
      setLoading(true);
      const res = await api.get('/preventive/entries', {
        params: { year: selectedYear, equipment_id: selectedEquipmentId }
      });
      setEntries(res.data);
    } catch (e) { toast.error('Eroare la incarcarea datelor'); }
    finally { setLoading(false); }
  }, [selectedYear, selectedEquipmentId]);

  const autoGenerateRef = useRef({});
  const autoGenerateYear = useCallback(async (year) => {
    if (autoGenerateRef.current[year]) return;
    try {
      const checkRes = await api.get('/preventive/entries', { params: { year, equipment_id: equipment[0]?.id } });
      if (checkRes.data && checkRes.data.length > 0) {
        autoGenerateRef.current[year] = true;
        return;
      }
      autoGenerateRef.current[year] = true;
      toast.info(`Se genereaza intrarile pentru ${year}...`);
      await api.post('/preventive/generate', { year });
      toast.success(`Intrari generate pentru ${year}`);
      fetchEntries();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail) toast.error(detail);
    }
  }, [equipment, fetchEntries]);

  useEffect(() => { fetchBase(); }, [fetchBase]);
  // Real-time: refresh entries on PM changes
  useRealtimeEvent('pm_change', fetchEntries);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => {
    if (equipment.length > 0 && hasRole('admin', 'manager')) {
      autoGenerateYear(selectedYear);
    }
  }, [selectedYear, equipment.length, autoGenerateYear, hasRole]);

  const locations = useMemo(() =>
    [...new Set(equipment.map(e => e.location_name || e.plant_name || '').filter(Boolean))].sort(), [equipment]);

  const filteredEquipment = useMemo(() => equipment.filter(e => {
    const loc = e.location_name || e.plant_name || '';
    if (locationFilter !== 'all' && loc !== locationFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return e.name.toLowerCase().includes(s) || (e.code||'').toLowerCase().includes(s) || loc.toLowerCase().includes(s);
    }
    return true;
  }), [equipment, locationFilter, searchTerm]);

  const monthlySheets = useMemo(() => {
    const map = {};
    entries.forEach(e => {
      if (!map[e.month]) map[e.month] = { month: e.month, entries: [], validated: 0, total: 0 };
      map[e.month].entries.push(e);
      map[e.month].total++;
      if (e.status === 'validated') map[e.month].validated++;
    });
    return map;
  }, [entries]);

  const selectedEquipment = equipment.find(e => e.id === selectedEquipmentId);
  const currentMonth = new Date().getMonth() + 1;
  const totalEntries = entries.length;
  const validatedCount = entries.filter(e => e.status === 'validated').length;
  const overdueCount = entries.filter(e => e.status === 'pending' && e.month < currentMonth).length;
  const currentMonthSheet = selectedMonth ? monthlySheets[selectedMonth] : null;

  // Actions
  const handleSelectEquipment = (eqId) => {
    setSelectedEquipmentId(eqId);
    setSelectedMonth(null);
    setMobileView('timeline');
  };

  const handleGenerate = async () => {
    if (!selectedEquipmentId) return;
    try {
      setGenerating(true);
      const res = await api.post('/preventive/generate-single', {
        year: selectedYear, month: parseInt(genMonth), equipment_id: selectedEquipmentId
      });
      toast.success(res.data.message);
      setShowGenerate(false);
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare la generare'); }
    finally { setGenerating(false); }
  };

  const handleGenerateYear = async () => {
    try {
      setGenerating(true);
      const res = await api.post('/preventive/generate', { year: selectedYear });
      toast.success(res.data.message);
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare la generare'); }
    finally { setGenerating(false); }
  };

  const handleOpenScanner = () => {
    setScanResult(null);
    setShowScanner(true);
  };

  const handleScanResult = async (scannedText) => {
    setScanResult({ text: scannedText, status: 'processing' });
    setValidating(true);
    try {
      const matchedEq = equipment.find(
        e => e.code === scannedText || e.id === scannedText ||
             (e.code && scannedText.includes(e.code)) ||
             (e.name && scannedText.includes(e.name))
      );
      const payload = { month: selectedMonth };
      if (matchedEq) {
        payload.equipment_id = matchedEq.id;
        const res = await api.post('/preventive/validate-qr', payload);
        setScanResult({ text: scannedText, status: 'success', message: res.data.message, equipment: matchedEq.name, count: res.data.validated_count });
        toast.success(res.data.message);
        setSelectedEquipmentId(matchedEq.id);
        setMobileView('timeline');
        fetchEntries();
      } else {
        payload.equipment_code = scannedText;
        const res = await api.post('/preventive/validate-qr', payload);
        setScanResult({ text: scannedText, status: 'success', message: res.data.message, count: res.data.validated_count });
        toast.success(res.data.message);
        fetchEntries();
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Echipament negasit sau fara intrari in asteptare';
      setScanResult({ text: scannedText, status: 'error', message: detail });
      toast.error(detail);
    } finally {
      setValidating(false);
    }
  };

  const handleValidateEntry = async (entryId) => {
    try {
      setDetailLoading(true);
      const res = await api.post('/preventive/validate-qr', { entry_id: entryId });
      toast.success(res.data.message);
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
    finally { setDetailLoading(false); }
  };

  const handleValidateWithOperator = async (data) => {
    try {
      setDetailLoading(true);
      const res = await api.post('/preventive/validate-with-operator', {
        entry_id: data.entryId,
        technician_id: data.technicianId,
        technician_name: data.technicianName,
        include_operator: data.includeOperator,
        operator_name: data.operatorName,
        operator_signature: data.operatorSignature
      });
      toast.success(res.data.message);
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare la validare'); }
    finally { setDetailLoading(false); }
  };

  // Set operator data in bulk (without validation)
  const handleSetOperatorBulk = async (data) => {
    try {
      setDetailLoading(true);
      const res = await api.post('/preventive/set-operator-bulk', {
        entry_ids: data.entryIds,
        operator_name: data.operatorName,
        operator_signature: data.operatorSignature
      });
      toast.success(res.data.message);
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare la setare operator'); }
    finally { setDetailLoading(false); }
  };

  const handleUpdateEntry = async (entryId, fields) => {
    try {
      await api.put(`/preventive/entries/${entryId}`, fields);
      toast.success('Salvat');
      fetchEntries();
    } catch (e) { toast.error('Eroare la salvare'); }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Sigur doriti stergerea?')) return;
    try {
      await api.delete(`/preventive/entries/${entryId}`);
      toast.success('Sters');
      fetchEntries();
    } catch (e) { toast.error('Eroare'); }
  };

  // Type CRUD
  const handleSaveType = async () => {
    try {
      if (editType) await api.put(`/preventive/maintenance-types/${editType.id}`, typeForm);
      else await api.post('/preventive/maintenance-types', typeForm);
      toast.success(editType ? 'Actualizat' : 'Creat');
      setShowTypeForm(false); setEditType(null);
      setTypeForm({ name: '', description: '', operatiuni: '' });
      fetchBase();
    } catch (e) { toast.error('Eroare'); }
  };
  const handleDeleteType = async (id) => {
    if (!window.confirm('Sigur?')) return;
    try { await api.delete(`/preventive/maintenance-types/${id}`); toast.success('Sters'); fetchBase(); }
    catch (e) { toast.error('Eroare'); }
  };

  // Alerts
  const loadAlerts = async () => {
    try {
      const [cfgRes, dashRes, histRes] = await Promise.all([
        api.get('/preventive/alert-config'),
        api.get('/preventive/alert-dashboard'),
        api.get('/preventive/alert-history?limit=20'),
      ]);
      setAlertConfig(cfgRes.data);
      setAlertDashboard(dashRes.data);
      setAlertHistory(histRes.data);
    } catch (e) { console.error(e); }
  };
  const saveAlerts = async () => {
    try { await api.post('/preventive/alert-config', alertConfig); toast.success('Salvat'); } catch (e) { toast.error('Eroare'); }
  };
  const sendAlerts = async () => {
    try { setSendingAlerts(true); const r = await api.post('/preventive/send-alerts'); toast.success(r.data.message); loadAlerts(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); } finally { setSendingAlerts(false); }
  };
  const exportPdf = async () => {
    if (!selectedEquipmentId) return;
    try {
      const params = { equipment_id: selectedEquipmentId, year: selectedYear };
      if (headerConfig.override_date) params.override_date = headerConfig.override_date;
      if (headerConfig.cod_document) params.override_cod = headerConfig.cod_document;
      if (headerConfig.anexa) params.override_anexa = headerConfig.anexa;
      const res = await api.get('/preventive/grafic/pdf', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a'); link.href = url;
      link.download = `Grafic_Intretinere_${selectedEquipment?.name||'utilaj'}_${selectedYear}.pdf`;
      link.click(); window.URL.revokeObjectURL(url); toast.success('PDF exportat');
    } catch (e) { toast.error('Eroare la export PDF'); }
  };

  const loadHeaderConfig = async () => {
    try {
      const res = await api.get('/preventive/pdf-header-settings');
      setHeaderConfig(prev => ({ ...prev, cod_document: res.data.cod_document || '', anexa: res.data.anexa || '' }));
    } catch (e) { /* ignore */ }
  };

  const loadKpi = async () => {
    try {
      const res = await api.get('/preventive/dashboard-kpi', { params: { year: selectedYear } });
      setKpiData(res.data);
    } catch (e) { console.error(e); }
  };
  const loadTechnicians = async () => {
    try {
      const res = await api.get('/preventive/technician-history', { params: { year: selectedYear } });
      setTechData(res.data);
    } catch (e) { console.error(e); }
  };
  const loadCalendar = async () => {
    try {
      const res = await api.get('/preventive/calendar', { params: { year: selectedYear } });
      setCalendarData(res.data);
    } catch (e) { console.error(e); }
  };
  const exportExcel = async () => {
    try {
      const res = await api.get('/preventive/export-excel', { params: { year: selectedYear }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url;
      link.download = `Preventiva_${selectedYear}.xlsx`;
      link.click(); window.URL.revokeObjectURL(url); toast.success('Excel exportat');
    } catch (e) { toast.error('Eroare export Excel'); }
  };

  const loadOverdue = async () => {
    try {
      const res = await api.get('/preventive/overdue-dashboard', { params: { year: selectedYear } });
      setOverdueData(res.data);
    } catch (e) { console.error(e); }
  };

  const loadPendingEntries = async (month, status) => {
    try {
      const params = { year: selectedYear };
      if (month) params.month = month;
      params.status = status || bulkStatusFilter || 'pending';
      const res = await api.get('/preventive/pending-entries', { params });
      setPendingEntries(res.data);
      setBulkSelected([]);
    } catch (e) { console.error(e); }
  };

  const switchView = (view) => {
    setRightView(view);
    setSelectedEquipmentId('');
    setSelectedMonth(null);
    if (view === 'kpi') loadKpi();
    else if (view === 'technicians') loadTechnicians();
    else if (view === 'calendar') loadCalendar();
    else if (view === 'overdue') loadOverdue();
    else if (view === 'bulk') loadPendingEntries();
  };

  const handleBulkValidate = async () => {
    if (bulkSelected.length === 0) return toast.error('Selectati cel putin o intrare');
    if (!bulkTechId) return toast.error('Selectati un tehnician');
    setBulkValidating(true);
    try {
      const res = await api.post('/preventive/admin-bulk-validate', {
        entry_ids: bulkSelected,
        technician_id: bulkTechId,
        technician_name: bulkTechName,
        date: bulkDateMode === 'random' ? 'random' : bulkCustomDate,
        year: selectedYear,
      });
      toast.success(res.data.message);
      setBulkSelected([]);
      loadPendingEntries(bulkMonth || undefined, bulkStatusFilter);
      loadOverdue();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
    finally { setBulkValidating(false); }
  };

  const handleBulkUpdate = async () => {
    if (bulkSelected.length === 0) return toast.error('Selectati cel putin o intrare');
    setBulkValidating(true);
    try {
      let value = {};
      if (bulkAction === 'change_date') {
        value = { date: bulkCustomDate, field: bulkDateField };
      } else if (bulkAction === 'change_technician') {
        value = { technician_id: bulkTechId, technician_name: bulkTechName };
      } else if (bulkAction === 'change_operator') {
        value = { operator_name: bulkOperatorName };
      } else if (bulkAction === 'change_status') {
        value = { status: bulkNewStatus };
      } else if (bulkAction === 'clear_data') {
        // Clear all data - reset to pending with empty fields
        value = { reset: true };
      }
      const res = await api.post('/preventive/bulk-update', {
        entry_ids: bulkSelected, action: bulkAction, value
      });
      toast.success(res.data.message);
      setBulkSelected([]);
      loadPendingEntries(bulkMonth || undefined, bulkStatusFilter);
      if (bulkAction === 'change_status' || bulkAction === 'clear_data') loadOverdue();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
    finally { setBulkValidating(false); }
  };

  // ================================================================
  // RENDER
  // ================================================================
  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="preventive-entries-page">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          {mobileView !== 'list' && (
            <button className="md:hidden p-1" onClick={() => setMobileView(selectedEquipmentId && mobileView === 'detail' ? 'timeline' : 'list')}>
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
          )}
          <div>
            <h1 className="text-base sm:text-lg font-bold text-slate-900" data-testid="page-title">Preventiva</h1>
            <p className="text-[11px] text-slate-500 hidden sm:block">Grafic intretinere lunara utilaje</p>
          </div>
        </div>
        <div className="flex gap-1.5 items-center">
          <div className="hidden sm:flex gap-0.5 bg-slate-100 rounded-lg p-0.5 mr-2">
            {[
              { key: 'equipment', icon: Wrench, label: 'Utilaje' },
              { key: 'overdue', icon: AlertTriangle, label: 'Restante' },
              { key: 'kpi', icon: PieChart, label: 'KPI' },
              { key: 'technicians', icon: Users, label: 'Tehnicieni' },
              { key: 'calendar', icon: CalendarDays, label: 'Calendar' },
            ].map(v => (
              <button key={v.key} onClick={() => v.key === 'equipment' ? setRightView('equipment') : switchView(v.key)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  rightView === v.key ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`} data-testid={`view-${v.key}`}>
                <v.icon className="w-3.5 h-3.5" />{v.label}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={exportExcel} data-testid="export-excel-btn">
            <FileSpreadsheet className="w-3.5 h-3.5" /><span className="hidden lg:inline">Excel</span>
          </Button>
          {hasRole('admin', 'manager') && (
            <>
              <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => switchView('bulk')} data-testid="bulk-validate-btn">
                <Shield className="w-3.5 h-3.5" /><span className="hidden lg:inline">Bulk</span>
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => { loadAlerts(); setShowSettings(true); setSettingsTab('alerts'); }} data-testid="alerts-btn">
                <Bell className="w-3.5 h-3.5" /><span className="hidden sm:inline">Alerte</span>
              </Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setShowSettings(true); setSettingsTab('types'); }} data-testid="settings-btn">
                <Settings className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Split layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT PANEL - Equipment list */}
        <div className={`w-full md:w-80 lg:w-96 border-r bg-slate-50/50 flex flex-col flex-shrink-0 ${
          mobileView !== 'list' ? 'hidden md:flex' : 'flex'
        }`}>
          <div className="p-3 space-y-2 border-b bg-white">
            <div className="flex gap-2">
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="flex-1 h-8 text-xs" data-testid="location-filter">
                  <SelectValue placeholder="Sectia..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate sectiile</SelectItem>
                  {locations.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-slate-400" />
              <Input placeholder="Cauta utilaj..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="pl-8 h-8 text-xs" data-testid="search-equipment" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredEquipment.map(eq => {
              const isActive = eq.id === selectedEquipmentId;
              return (
                <button key={eq.id} onClick={() => handleSelectEquipment(eq.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left border-b border-slate-100 transition-colors ${
                    isActive ? 'bg-blue-50 border-l-[3px] border-l-blue-600' : 'hover:bg-slate-50 border-l-[3px] border-l-transparent'
                  }`} data-testid={`eq-${eq.id}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-blue-600' : 'bg-slate-200'}`}>
                    <Wrench className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm font-medium block truncate ${isActive ? 'text-blue-900' : 'text-slate-800'}`}>{eq.name}</span>
                    <span className="text-[11px] text-slate-400 font-mono">{eq.code} · {eq.location_name || eq.plant_name}</span>
                  </div>
                </button>
              );
            })}
            {filteredEquipment.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-400">Niciun utilaj gasit</div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL - Content */}
        <div className={`flex-1 flex flex-col overflow-hidden bg-white ${
          mobileView === 'list' ? 'hidden md:flex' : 'flex'
        }`}>
          {rightView === 'overdue' && <OverdueDashboardView data={overdueData} year={selectedYear} />}

          {rightView === 'bulk' && (
            <BulkValidationView
              entries={pendingEntries}
              users={users}
              year={selectedYear}
              selected={bulkSelected}
              setSelected={setBulkSelected}
              techId={bulkTechId}
              setTechId={setBulkTechId}
              techName={bulkTechName}
              setTechName={setBulkTechName}
              dateMode={bulkDateMode}
              setDateMode={setBulkDateMode}
              customDate={bulkCustomDate}
              setCustomDate={setBulkCustomDate}
              bulkMonth={bulkMonth}
              setBulkMonth={(m) => { setBulkMonth(m); loadPendingEntries(m || undefined, bulkStatusFilter); }}
              onValidate={handleBulkValidate}
              onBulkUpdate={handleBulkUpdate}
              validating={bulkValidating}
              bulkAction={bulkAction}
              setBulkAction={setBulkAction}
              statusFilter={bulkStatusFilter}
              setStatusFilter={(s) => { setBulkStatusFilter(s); loadPendingEntries(bulkMonth || undefined, s); }}
              operatorName={bulkOperatorName}
              setOperatorName={setBulkOperatorName}
              newStatus={bulkNewStatus}
              setNewStatus={setBulkNewStatus}
              dateField={bulkDateField}
              setDateField={setBulkDateField}
            />
          )}

          {rightView === 'kpi' && <KpiDashboardView data={kpiData} year={selectedYear} />}
          {rightView === 'technicians' && <TechnicianHistoryView data={techData} year={selectedYear} />}
          {rightView === 'calendar' && <CalendarView data={calendarData} year={selectedYear} />}

          {/* Equipment View (default) */}
          {rightView === 'equipment' && !selectedEquipmentId ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Camera className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                <p className="text-sm font-medium text-slate-500">Selectati un utilaj sau scanati QR</p>
                <p className="text-xs text-slate-400 mt-1">din lista din stanga sau folosind camera</p>
                <Button className="mt-4 gap-2" onClick={handleOpenScanner} data-testid="scan-qr-main-btn">
                  <Camera className="w-4 h-4" /> Scaneaza QR utilaj
                </Button>
              </div>
            </div>
          ) : rightView === 'equipment' && selectedEquipmentId ? (
            <>
              <div className="px-4 py-3 border-b bg-white flex-shrink-0">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-slate-900">{selectedEquipment?.name}</h2>
                      <p className="text-xs text-slate-500">
                        <span className="font-mono text-blue-600">{selectedEquipment?.code}</span>
                        <span className="mx-1.5">·</span>
                        {selectedEquipment?.location_name || selectedEquipment?.plant_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(parseInt(v))}>
                      <SelectTrigger className="w-[80px] h-8 text-xs" data-testid="year-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[...Array(5)].map((_, i) => { const y = new Date().getFullYear() - 1 + i; return <SelectItem key={y} value={String(y)}>{y}</SelectItem>; })}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={exportPdf} data-testid="export-pdf-btn">
                      <Download className="w-3.5 h-3.5" /><span className="hidden lg:inline">PDF</span>
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => { loadHeaderConfig(); setShowHeaderConfig(true); }} data-testid="header-config-btn">
                      <FileText className="w-3.5 h-3.5" /><span className="hidden lg:inline">Antet</span>
                    </Button>
                    {hasRole('admin', 'manager') && totalEntries === 0 && (
                      <Button size="sm" className="h-8 text-xs gap-1" onClick={handleGenerateYear} disabled={generating} data-testid="generate-year-toolbar-btn">
                        <Plus className="w-3.5 h-3.5" /><span className="hidden lg:inline">{generating ? 'Se genereaza...' : 'Genereaza An'}</span>
                      </Button>
                    )}
                  </div>
                </div>

                {totalEntries > 0 && (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${(validatedCount / totalEntries * 100)}%` }} />
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <span className="text-xs font-semibold text-emerald-700">{validatedCount} validate</span>
                      {overdueCount > 0 && <span className="text-xs font-semibold text-red-600">{overdueCount} restante</span>}
                      <span className="text-xs text-slate-400">{totalEntries} total</span>
                    </div>
                  </div>
                )}
              </div>

              {/* ANNUAL TIMELINE + DETAIL */}
              <div className={`flex-1 overflow-y-auto ${mobileView === 'detail' ? '' : ''}`}>
                {loading ? (
                  <div className="flex items-center justify-center h-40 text-slate-400 text-sm">Se incarca...</div>
                ) : totalEntries === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64">
                    <Calendar className="w-12 h-12 text-slate-200 mb-3" />
                    <p className="text-sm font-medium text-slate-500">Nicio intrare generata pentru {selectedYear}</p>
                    <p className="text-xs text-slate-400 mt-1">Generati intrarile de intretinere pentru anul curent</p>
                    {hasRole('admin', 'manager') && (
                      <Button className="mt-4 text-xs gap-1" onClick={handleGenerateYear} disabled={generating} data-testid="generate-year-btn">
                        <Plus className="w-3.5 h-3.5" /> {generating ? 'Se genereaza...' : `Genereaza ${selectedYear}`}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className={`${mobileView === 'detail' ? 'hidden md:block' : ''}`}>
                    {/* Timeline Grid - 12 months */}
                    <div className="p-4">
                      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-1.5" data-testid="month-timeline">
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(month => {
                          const sheet = monthlySheets[month];
                          const hasEntries = sheet && sheet.total > 0;
                          const allValidated = hasEntries && sheet.validated === sheet.total;
                          const hasOverdue = hasEntries && !allValidated && month < currentMonth;
                          const isCurrent = month === currentMonth;
                          const isSelected = selectedMonth === month;
                          return (
                            <button key={month} onClick={() => { setSelectedMonth(month); setMobileView('detail'); }}
                              className={`relative rounded-lg p-2 text-center transition-all border-2 ${
                                isSelected ? 'border-blue-600 shadow-md scale-105' :
                                allValidated ? 'border-emerald-300 bg-emerald-50' :
                                hasOverdue ? 'border-red-300 bg-red-50' :
                                isCurrent ? 'border-blue-300 bg-blue-50' :
                                hasEntries ? 'border-slate-200 bg-white hover:border-slate-300' :
                                'border-dashed border-slate-200 bg-slate-50/50'
                              }`}
                              data-testid={`month-btn-${month}`}>
                              <div className={`text-xs font-bold ${
                                allValidated ? 'text-emerald-700' : hasOverdue ? 'text-red-700' : isCurrent ? 'text-blue-700' : 'text-slate-600'
                              }`}>{MONTHS_SHORT[month - 1]}</div>
                              {hasEntries && (
                                <div className="mt-1">
                                  {allValidated ? (
                                    <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                                  ) : hasOverdue ? (
                                    <AlertTriangle className="w-4 h-4 text-red-500 mx-auto" />
                                  ) : (
                                    <div className="text-[10px] text-slate-400">{sheet.validated}/{sheet.total}</div>
                                  )}
                                </div>
                              )}
                              {!hasEntries && <div className="mt-1 text-[10px] text-slate-300">—</div>}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {selectedMonth && currentMonthSheet && (
                      <MonthlySheet
                        sheet={currentMonthSheet}
                        month={selectedMonth}
                        year={selectedYear}
                        currentMonth={currentMonth}
                        users={users}
                        currentUserId={user?.id}
                        onValidateEntry={handleValidateEntry}
                        onSetOperatorBulk={handleSetOperatorBulk}
                        onOpenScanner={handleOpenScanner}
                        onUpdateEntry={handleUpdateEntry}
                        onDeleteEntry={handleDeleteEntry}
                        detailLoading={detailLoading}
                        hasRole={hasRole}
                      />
                    )}
                    {selectedMonth && !currentMonthSheet && (
                      <div className="px-4 pb-4">
                        <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
                          <Calendar className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                          <p className="text-sm text-slate-500">Nicio intrare pentru {MONTHS_RO[selectedMonth - 1]}</p>
                          {hasRole('admin', 'manager') && (
                            <Button size="sm" className="mt-3 text-xs" onClick={handleGenerateYear} disabled={generating}>
                              <Plus className="w-3.5 h-3.5 mr-1" /> {generating ? 'Se genereaza...' : 'Genereaza intrari'}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Mobile detail view */}
                {mobileView === 'detail' && selectedMonth && currentMonthSheet && (
                  <div className="md:hidden">
                    <MonthlySheet
                      sheet={currentMonthSheet}
                      month={selectedMonth}
                      year={selectedYear}
                      currentMonth={currentMonth}
                      users={users}
                      currentUserId={user?.id}
                      onValidateEntry={handleValidateEntry}
                      onSetOperatorBulk={handleSetOperatorBulk}
                      onOpenScanner={handleOpenScanner}
                      onUpdateEntry={handleUpdateEntry}
                      onDeleteEntry={handleDeleteEntry}
                      detailLoading={detailLoading}
                      hasRole={hasRole}
                    />
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* === DIALOGS === */}
      <GenerateDialog open={showGenerate} onOpenChange={setShowGenerate} genMonth={genMonth} setGenMonth={setGenMonth}
        onGenerate={handleGenerate} generating={generating} equipmentName={selectedEquipment?.name} />

      <SettingsDialog open={showSettings} onOpenChange={setShowSettings} tab={settingsTab} setTab={setSettingsTab}
        maintenanceTypes={maintenanceTypes} alertConfig={alertConfig} setAlertConfig={setAlertConfig}
        alertDashboard={alertDashboard} alertHistory={alertHistory} newRecipient={newRecipient} setNewRecipient={setNewRecipient}
        onSaveAlerts={saveAlerts} onSendAlerts={sendAlerts} sendingAlerts={sendingAlerts} loadAlerts={loadAlerts}
        showTypeForm={showTypeForm} setShowTypeForm={setShowTypeForm} editType={editType} setEditType={setEditType}
        typeForm={typeForm} setTypeForm={setTypeForm} onSaveType={handleSaveType} onDeleteType={handleDeleteType}
        hasRole={hasRole} />

      <QrScannerDialog
        open={showScanner}
        onOpenChange={setShowScanner}
        qrScannerRef={qrScannerRef}
        scanResult={scanResult}
        validating={validating}
        onScanResult={handleScanResult}
        onRescan={() => setScanResult(null)}
      />

      {/* === HEADER CONFIG DIALOG === */}
      <Dialog open={showHeaderConfig} onOpenChange={setShowHeaderConfig}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Configurare Antet PDF</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-slate-500">Cod document</Label>
              <Input className="h-8 text-sm" value={headerConfig.cod_document} onChange={e => setHeaderConfig({...headerConfig, cod_document: e.target.value})} placeholder="F-66-16" data-testid="header-cod-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Anexa</Label>
              <Input className="h-8 text-sm" value={headerConfig.anexa} onChange={e => setHeaderConfig({...headerConfig, anexa: e.target.value})} placeholder="Anexa PP-7.1.3" data-testid="header-anexa-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Data antet (override - gol = automat din prima validare)</Label>
              <Input className="h-8 text-sm" value={headerConfig.override_date} onChange={e => setHeaderConfig({...headerConfig, override_date: e.target.value})} placeholder="ex: 14.01.2026" data-testid="header-date-input" />
              <p className="text-[10px] text-slate-400 mt-0.5">Lasati gol pentru data automata din prima validare</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowHeaderConfig(false)}>Inchide</Button>
            <Button size="sm" onClick={async () => {
              try {
                await api.put('/preventive/pdf-header-settings', { cod_document: headerConfig.cod_document, anexa: headerConfig.anexa });
                toast.success('Setari antet salvate');
                setShowHeaderConfig(false);
              } catch (e) { toast.error('Eroare la salvare'); }
            }} data-testid="save-header-btn">Salveaza</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreventiveEntriesPage;
